import { CourseTreeNodeTypeEnum, type CourseTreeNode } from "@/types/generated";
import { extractLeadingNumber } from "@/lib/courseUtils";
import { storyboardTitle } from "@/lib/storyboardTitle";
import { listSubChapterStoryboards } from "@/api/storyboards";
import type { LoadedCourse } from "@/stores/courseDetailStore";
import type { Storyboard, TextFlashcardsData } from "@/types/storyboard";

export interface Deck {
  id: string;
  moduleId: string;
  /** 1-based place of the module among course modules, including modules with no collected decks. */
  moduleIndex: number;
  moduleTitle: string;
  subchapterTitle: string;
  deckTitle: string;
  totalCards: number;
  introduction: string[];
  instruction?: string;
  cards: { frontside: string; backside: string }[];
}

export interface DeckModuleGroup {
  moduleId: string;
  moduleIndex: number;
  moduleTitle: string;
  decks: Deck[];
}

const collectSubchapters = (node: CourseTreeNode): CourseTreeNode[] => {
  if (node.type === CourseTreeNodeTypeEnum.Subchapter) return [node];
  return (node.children ?? []).flatMap(collectSubchapters);
};

const collectContentElementIds = (node: CourseTreeNode): string[] => {
  if (node.type === CourseTreeNodeTypeEnum.ContentElement) return [node.id];
  return (node.children ?? []).flatMap(collectContentElementIds);
};

const findContentElement = (
  node: CourseTreeNode,
  id: string
): CourseTreeNode | null => {
  if (node.id === id) return node;
  for (const child of node.children ?? []) {
    const found = findContentElement(child, id);
    if (found) return found;
  }
  return null;
};

const isFlashcardStoryboard = (
  s: Storyboard
): s is Storyboard & {
  contentType: "TEXT";
  subType: "FLASHCARDS";
  text: TextFlashcardsData;
} => s.contentType === "TEXT" && s.subType === "FLASHCARDS";

const toCompletedSet = (
  completedContentElementIds: ReadonlySet<string> | readonly string[]
): ReadonlySet<string> =>
  completedContentElementIds instanceof Set
    ? completedContentElementIds
    : new Set(completedContentElementIds);

/**
 * Collects Lernkarten decks whose content elements are marked completed
 * (flip-all in the lesson, or historically completed LearningProgress).
 */
export const collectDecks = async (
  courseStructure: Pick<LoadedCourse, "structure">,
  completedContentElementIds: ReadonlySet<string> | readonly string[]
): Promise<Deck[]> => {
  const completed = toCompletedSet(completedContentElementIds);
  if (completed.size === 0) {
    return [];
  }

  const modules = courseStructure.structure.filter(
    (n) => n.type === CourseTreeNodeTypeEnum.Module
  );

  const decks: Deck[] = [];

  for (const [moduleOffset, moduleNode] of modules.entries()) {
    const moduleIndex = moduleOffset + 1;

    const subchapters = collectSubchapters(moduleNode).filter((subchapter) =>
      collectContentElementIds(subchapter).some((id) => completed.has(id))
    );
    if (subchapters.length === 0) {
      continue;
    }

    const storyboardLists = await Promise.all(
      subchapters.map((sc) =>
        // A subchapter whose storyboards cannot be loaded adds no cards.
        listSubChapterStoryboards(sc.id)
          .catch((): Storyboard[] => [])
          .then((sbs) => ({ subchapter: sc, storyboards: sbs }))
      )
    );

    for (const { subchapter, storyboards } of storyboardLists) {
      for (const sb of storyboards) {
        if (!isFlashcardStoryboard(sb)) continue;
        if (!completed.has(sb.contentElementId)) continue;
        const data = sb.text;
        if (!data?.cards?.length) continue;

        const ce = findContentElement(moduleNode, sb.contentElementId);
        decks.push({
          id: sb.contentElementId,
          moduleId: moduleNode.id,
          moduleIndex,
          moduleTitle: moduleNode.title,
          subchapterTitle: subchapter.title,
          deckTitle: storyboardTitle(data.title, ce?.title || subchapter.title),
          totalCards: data.cards.length,
          introduction: data.introduction ?? [],
          instruction: data.instruction,
          cards: data.cards,
        });
      }
    }
  }

  return decks;
};

/**
 * Groups collected Lernkarten by course module, in collection order.
 * Used to label the picker list without merging modules that share a title.
 */
export const groupDecksByModule = (
  decks: readonly Deck[]
): DeckModuleGroup[] => {
  const groups: DeckModuleGroup[] = [];

  for (const deck of decks) {
    const group = groups.find(
      (candidate) => candidate.moduleId === deck.moduleId
    );
    if (group) {
      group.decks.push(deck);
      continue;
    }

    groups.push({
      moduleId: deck.moduleId,
      moduleIndex: deck.moduleIndex,
      moduleTitle: deck.moduleTitle,
      decks: [deck],
    });
  }

  return groups;
};

/**
 * Heading for one module in the collected Lernkarten list.
 * A leading number on the factory title is dropped so it is not shown twice.
 */
export const moduleGroupHeading = (
  moduleIndex: number,
  moduleTitle: string
): string => {
  const titleWithoutLeadingNumber =
    extractLeadingNumber(moduleTitle).rest.trim();
  const name = titleWithoutLeadingNumber || moduleTitle;

  return `Modul ${moduleIndex} ${name}`;
};
